import type { LensCardConfig } from '~/types/leLens';

/**
 * The only card config keys a model may set.
 *
 * Presentation config is the author's, not AskRichard's. A value format is the
 * exception because it is a property of the query the model just wrote: it knows
 * whether a column is dollars, a percentage already multiplied by 100, or a 0 to
 * 1 ratio, and nobody else does without reading the SQL.
 *
 * Everything else on a card is a judgement about how a number should be read,
 * and two of them can mislead outright. A truncated axis floor exaggerates
 * change and an axis ceiling flattens it. A person who sets one of those has
 * made a choice they can be asked about; a model that sets one has made the
 * chart argue something nobody chose. So the boundary is an allowlist rather
 * than a denylist, and a new config key is author-only until someone adds it
 * here on purpose.
 */
export const MODEL_WRITABLE_CONFIG_KEYS = ['format'] as const;

/**
 * Reduces a config a model produced to the keys it is allowed to set.
 *
 * Returns undefined rather than an empty object so a card can omit `config`
 * entirely, which is what a stored spec should look like when the model set
 * nothing.
 */
export function pickModelConfig(raw: unknown): LensCardConfig | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const source = raw as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  for (const key of MODEL_WRITABLE_CONFIG_KEYS) {
    if (source[key] !== undefined) {
      picked[key] = source[key];
    }
  }
  return Object.keys(picked).length ? (picked as LensCardConfig) : undefined;
}

/** Spreadable, so a card omits `config` rather than carrying an empty one. */
export function configEntry(config: LensCardConfig | undefined): { config?: LensCardConfig } {
  return config ? { config } : {};
}
